import Timeline from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineOppositeContent, {
  timelineOppositeContentClasses,
} from '@mui/lab/TimelineOppositeContent';
import { Box, Typography, alpha } from '@mui/material';
import { motion } from 'framer-motion';
import useVisibilityOnScroll from '../hooks/useVisibilityOnScroll';

interface CustomizedTimelineProps {
    data: {
        role: string;
        company: string;
        duration: string;
    }[]
}

const MotionTimelineItem = motion(TimelineItem);

const CustomizedTimeline = ({ data }: CustomizedTimelineProps) => {
  const [timelineRef, isTimelineVisible] = useVisibilityOnScroll(0.2);

  const itemVariants = {
    hidden: { opacity: 0, x: -30 },
    visible: (i: number) => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: i * 0.15,
        duration: 0.5,
        ease: [0.4, 0, 0.2, 1] as any
      }
    })
  };

  return (
    <Box ref={timelineRef}>
      <Timeline
        sx={{
          [`& .${timelineOppositeContentClasses.root}`]: {
            flex: 0.5,
          },
        }}
      >
        {data.map((experience, index) => (
          <MotionTimelineItem
            key={index}
            custom={index}
            variants={itemVariants}
            initial="hidden"
            animate={isTimelineVisible ? "visible" : "hidden"}
            whileHover={{ scale: 1.02 }}
            sx={{
              display: 'flex',
              p: 1.5,
              borderRadius: 2,
              transition: 'all 0.3s',
              '&:hover': {
                backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.6),
                backdropFilter: 'blur(10px)',
              },
            }}
          >
            <TimelineOppositeContent
              sx={{
                color: 'text.secondary',
                fontSize: '14px',
                fontWeight: 500,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {experience.duration}
              </Typography>
            </TimelineOppositeContent>
            <TimelineSeparator>
              <TimelineDot
                variant='outlined'
                color="secondary"
                sx={{
                  borderWidth: 2.5,
                  width: 14,
                  height: 14,
                  boxShadow: (theme) => `0 0 0 4px ${alpha(theme.palette.secondary.main, 0.1)}`,
                }}
              />
              {index < data.length - 1 && (
                <TimelineConnector
                  sx={{
                    backgroundColor: (theme) => alpha(theme.palette.secondary.main, 0.3),
                    width: 2,
                  }}
                />
              )}
            </TimelineSeparator>
            <TimelineContent sx={{ pb: 3 }}>
              <Typography
                variant='h6'
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  mb: 0.5,
                }}
              >
                {experience.role}
              </Typography>
              <Typography
                variant='body2'
                sx={{
                  color: 'text.secondary',
                  fontWeight: 500,
                }}
              >
                {experience.company}
              </Typography>
            </TimelineContent>
          </MotionTimelineItem>
        ))}
      </Timeline>
    </Box>
  );
}

export default CustomizedTimeline;